import React from "react";
import { Grid, Paper, makeStyles } from "@material-ui/core";
import {
    summaryScreenName,
    screenById,
    screenRelevant,
    screenHasErrors,
    screenIsComplete,
} from "../screens/screens";
import { Colors } from "../styles";

import CheckIcon from "../assets/images/check_icon.svg";
import InProgressActiveIcon from "../assets/images/in-progress-active_icon.svg";
import InProgressIcon from "../assets/images/in-progress_icon.svg";
import ChevronIcon from "../assets/images/chevron_icon.svg";
import { Configuration } from "../clients/FirebaseClient/types";
import { useCurrentConfiguration } from "../utils/hooks";

const useStyles = makeStyles(() => ({
    container: {
        margin: 0,
        padding: 24,
        fontFamily: "innogyWEB",
    },
    itemContainer: {
        margin: 0,
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 12,
        paddingBottom: 12,
        borderColor: Colors.anthracite25,
        borderTop: "1px solid",
        fontFamily: "innogyWEB",
    },
    itemRow: {
        display: "flex",
        alignItems: "center",
    },
    title: {
        marginLeft: 24,
        verticalAlign: "middle",
        textDecoration: "inherit",
        fontSize: 22,
        fontWeight: 500,
        alignSelf: "center",
    },
    item: {
        marginLeft: 24,
        textDecoration: "inherit",
        fontSize: 22,
        fontWeight: 200,
    },
    hint: {
        marginTop: 16,
        fontSize: 22,
        fontWeight: 200,
    },
}));

const isProductClassesAndProductTypes = (configuration: Configuration): boolean => {
    return (
        configuration !== undefined &&
        configuration.state.productClasses !== undefined &&
        configuration.state.productTypes !== undefined &&
        (configuration.state.productTypes.state.value.adjustableLoad ||
            configuration.state.productTypes.state.value.increase ||
            ((configuration.state.productClasses?.state.value.gas ||
                configuration.state.productClasses?.state.value.power) &&
                (configuration.state.productTypes?.state.value.modification ||
                    configuration.state.productTypes?.state.value.newConnection ||
                    configuration.state.productTypes?.state.value.removal)))
    );
};

const isSummaryVisible = (configuration: Configuration): boolean => {
    return (
        configuration !== undefined &&
        configuration.state.buildingAddress !== undefined &&
        !configuration.state.buildingAddress?.default
    );
};

const isConfigurationComplete = (configuration: Configuration): boolean => {
    return configuration !== undefined && configuration.complete;
};

const isDocumentsVisible = (configuration: Configuration): boolean => {
    return (
        configuration !== undefined &&
        Object.values(configuration.documents)
            .filter((doc) => doc.relevant)
            .map(() => 1)
            .reduce((a, b) => a + b, 0) > 0 &&
        isProductClassesAndProductTypes(configuration)
    );
};

const isDocumentsComplete = (configuration: Configuration): boolean => {
    return (
        configuration !== undefined &&
        Object.values(configuration.documents)
            .filter((doc) => doc.relevant && !doc.optional)
            .map((doc) => (doc.relevant && !doc.progress ? doc.files.length : 0))
            .every((x) => x > 0)
    );
};

const isAddressVisible = (configuration: Configuration): boolean => {
    return (
        configuration !== undefined &&
        configuration.state.buildingAddress !== undefined &&
        !configuration.state.buildingAddress?.default &&
        isProductClassesAndProductTypes(configuration)
    );
};

const isAddressComplete = (configuration: Configuration): boolean => {
    return (
        configuration !== undefined &&
        configuration.state.invoiceRecipient !== undefined &&
        !configuration.state.invoiceRecipient?.state.error
    );
};

const isDatesVisible = (configuration: Configuration): boolean => {
    return (
        configuration !== undefined &&
        screenById("dates").changes.some(
            (changeName) => configuration.state[changeName]?.relevant,
        ) &&
        isProductClassesAndProductTypes(configuration)
    );
};

interface ScreenGroup {
    [x: string]: {
        groups: string[];
        hint: string;
        additionalValidation?: (configuration: Configuration) => boolean;
        visibility?: (configuration: Configuration) => boolean;
    };
}

const screenGroups: ScreenGroup = {
    Netzanschluss: {
        groups: [
            "productTypes",
            "building_modification",
            "building_removal",
            "building_new_connection",
            "building_increase",
            "charger_connection_situation",
            "charger_connection_tech",
            "power_evaluation",
            "charger_power_evaluation",
            "increase_current_power_evaluation",
            "increase_expected_power_evaluation",
            "charger_technical_details",
            "connection_technology_modification",
            "connection_technology",
            "building_modification_power_cable",
            "multiline_connection",
        ],
        hint: "Benötigte Angaben zu Ihrem Netzanschluss",
    },
    Dokumente: {
        groups: ["documents"],
        hint: "Benötigte Dokumente",
        additionalValidation: isDocumentsComplete,
        visibility: isDocumentsVisible,
    },
    Adressen: {
        groups: ["addresses"],
        hint: "Auftraggeberdaten und Daten weiterer beteiligten Personen.",
        additionalValidation: isAddressComplete,
        visibility: isAddressVisible,
    },
    Termine: {
        groups: ["dates"],
        hint: "Termine und Zeiträume",
        visibility: isDatesVisible,
    },
    Zusammenfassung: {
        groups: [summaryScreenName],
        hint: "Gesammelte Daten Ihrer Netzanschluss Konfiguration",
        additionalValidation: isConfigurationComplete,
        visibility: isSummaryVisible,
    },
};

interface MenuItemGroupProps {
    name: string;
    items: string[];
    current: string;
    navigate: (screenName: string) => void;
    hint: string;
}
const MenuItemGroup = (props: MenuItemGroupProps): JSX.Element => {
    const classes = useStyles();
    const { hint } = props;
    const groupComplete = props.items
        .map((screenName) => screenIsComplete(screenById(screenName)))
        .every((x) => x);
    const groupActive = props.items.some((it) => it === props.current);

    const groupIcon = groupComplete
        ? CheckIcon
        : groupActive
        ? InProgressActiveIcon
        : InProgressIcon;

    return (
        <Grid item>
            <Paper elevation={2} style={{ borderRadius: 8 }}>
                <div className={classes.container}>
                    <div className={classes.itemRow}>
                        <img alt="" src={groupIcon} />
                        <span className={classes.title}>{props.name}</span>
                        <span style={{ marginLeft: "auto" }} />
                    </div>
                    <div className={classes.hint}>{hint}</div>
                </div>
                {props.items
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    .filter((screenId) => screenRelevant(screenById(screenId)!))
                    .map((it) => {
                        const screen = screenById(it);
                        const screenComplete = screenIsComplete(screen);
                        const screenActive = props.current === it;
                        const screenIcon = screenHasErrors(screen)
                            ? InProgressActiveIcon
                            : screenComplete
                            ? CheckIcon
                            : screenActive
                            ? InProgressActiveIcon
                            : InProgressIcon;
                        return (
                            <div
                                className={classes.itemContainer}
                                style={{
                                    backgroundColor:
                                        props.current === it ? Colors.anthracite5 : Colors.white,
                                    cursor: "pointer",
                                }}
                                onClick={() => props.navigate(it)}
                                key={it}
                            >
                                <div className={classes.itemRow}>
                                    <img alt="" src={screenIcon} />
                                    <span
                                        className={classes.item}
                                        style={{
                                            color: screenHasErrors(screen)
                                                ? Colors.radiantRed
                                                : Colors.anthracite,
                                        }}
                                    >
                                        {screen.name}
                                    </span>
                                    <img alt="" style={{ marginLeft: "auto" }} src={ChevronIcon} />
                                </div>
                            </div>
                        );
                    })}
                <div className={classes.itemContainer}></div>
            </Paper>
        </Grid>
    );
};

interface MenuListGroupProps {
    name: string;
    id: string;
    navigate: (screenName: string) => void;
    current: string;
    hint: string;
    additionalValidation?: (configuration: Configuration) => boolean;
}

const MenuListGroup = (props: MenuListGroupProps): JSX.Element => {
    const classes = useStyles();
    const configuration = useCurrentConfiguration();
    const { hint, additionalValidation, name } = props;
    const isActive = props.id === props.current;
    const isComplete = screenIsComplete(screenById(props.id));
    const locallyValid = additionalValidation ? additionalValidation(configuration) : true;
    const icon = !locallyValid
        ? InProgressActiveIcon
        : isComplete
        ? CheckIcon
        : isActive
        ? InProgressActiveIcon
        : InProgressIcon;
    return (
        <Grid item>
            <Paper
                onClick={() => props.navigate(props.id)}
                style={{
                    borderRadius: 8,
                    cursor: "pointer",
                    backgroundColor: isActive ? Colors.anthracite5 : Colors.white,
                }}
            >
                <div className={classes.container}>
                    <div className={classes.itemRow}>
                        <img alt="" src={icon} />
                        <span className={classes.title}>{name}</span>
                        <img alt="" style={{ marginLeft: "auto" }} src={ChevronIcon} />
                    </div>
                    <div className={classes.hint}>{hint}</div>
                </div>
            </Paper>
        </Grid>
    );
};

interface MenuGroupProps {
    groupName: string;
    navigate: (screenName: string) => void;
    current: string;
}

const MenuGroup = (props: MenuGroupProps): JSX.Element => {
    const group = screenGroups[props.groupName].groups;
    const hint = screenGroups[props.groupName].hint;
    const configuration = useCurrentConfiguration();
    const additionalValidation = screenGroups[props.groupName].additionalValidation;

    const visibility = screenGroups[props.groupName].visibility;
    const visible = visibility ? visibility(configuration) : true;
    if (!visible) return <div />;
    if (group.length > 1) {
        return (
            <MenuItemGroup
                name={props.groupName}
                items={group}
                navigate={props.navigate}
                current={props.current}
                hint={hint}
            />
        );
    } else {
        return (
            <MenuListGroup
                name={props.groupName}
                id={group[0]}
                navigate={props.navigate}
                current={props.current}
                hint={hint}
                additionalValidation={additionalValidation}
            />
        );
    }
};

interface MenuProps {
    navigate: (screenName: string) => void;
    current: string;
}

export const Menu = (props: MenuProps): JSX.Element => {
    return (
        <Grid container spacing={2} style={{ display: "flex", flexDirection: "column" }}>
            {Object.keys(screenGroups).map((it) => (
                <MenuGroup
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    groupName={it as any}
                    key={it}
                    navigate={props.navigate}
                    current={props.current}
                />
            ))}
        </Grid>
    );
};
